// @ts-strict-ignore
import { PUSHER_ERROR_STATUS } from "@dashboardApi/apis";
import type {
  DashboardApiEndpointDefinitions,
  dashboardApiEndpoints,
  FilteredDashboardApiEndpointKeys,
} from "@dashboardApi/apis";
import type { UseQueryOptions } from "@tanstack/react-query";

import type { FetchRequestError } from "@lib/errors";
import type { Awaited } from "@lib/typeHelpers";
import { FetchUserError } from "@lib/errors/FetchUserError";

export const delayOptions = <
  Endpoint extends FilteredDashboardApiEndpointKeys<typeof dashboardApiEndpoints, "POSTWS">,
  GetFetcherFunction extends DashboardApiEndpointDefinitions[Endpoint]["POSTWS"],
  TFnData = Awaited<ReturnType<(typeof dashboardApiEndpoints)[Endpoint]["POSTWS"]>>,
  TData = TFnData,
>(
  options: Omit<
    UseQueryOptions<
      // the return value from our fetcher function
      Awaited<ReturnType<GetFetcherFunction>>,
      // the type of `error` returned from `useQuery`
      FetchRequestError,
      // the type of `data` returned from `useQuery`
      TData,
      // the type of the query key, which is unused in this instance since we define it earlier, but it is here for logical consistency
      [Parameters<GetFetcherFunction>] extends [[{ queryKey: any }]]
        ? Parameters<GetFetcherFunction>[0]["queryKey"]
        : never
    >,
    "queryKey" | "queryFn"
  >,
  retryOpts?: {
    retryCount: number;
    delayBeforeRetry: number;
    expBackoff: boolean;
  }
) => {
  const { retryCount = 5, delayBeforeRetry = 1000, expBackoff = true } = retryOpts || {};
  // the adjusted failure count. If a request is restarted because pusher disconnected
  // we set this to the current failure number so that we can restart from 0
  const failureReset = { subtract: 0 };
  const adjustFailureCountIfNecessary = (failureCount: number, error: Error) => {
    if (error instanceof DOMException && error.name === "AbortError") {
      failureReset.subtract = failureCount + 1;
    }
    return failureCount - failureReset.subtract;
  };
  const newOptions: typeof options = {
    ...options,
    retry: (failureCount, error) => {
      const adjustedFailureCount = adjustFailureCountIfNecessary(failureCount, error);
      const retry = options.retry;
      if (typeof retry === "function") {
        return retry(adjustedFailureCount, error);
      }
      if (retry !== undefined) {
        if (retry === true || retry === false) {
          return retry;
        }
        return adjustedFailureCount < retry - 1;
      }

      if (
        error.status === PUSHER_ERROR_STATUS ||
        (error.status === 504 && error.statusText === "Request timeout") ||
        error instanceof FetchUserError
      ) {
        return false;
      }
      return adjustedFailureCount < retryCount - 1;
    },
    retryDelay: (attempt, error) => {
      const adjustedFailureCount = adjustFailureCountIfNecessary(attempt, error);
      const retryDelay = options.retryDelay;
      if (typeof retryDelay === "function") {
        return retryDelay(adjustedFailureCount, error);
      }
      if (retryDelay !== undefined) {
        return retryDelay;
      }

      // exponential backoff if requested
      const delay = expBackoff ? delayBeforeRetry * 2 ** adjustedFailureCount : delayBeforeRetry;

      // Add a random jitter up to 10% of the delay
      const jitter = delay * Math.random() * 0.1;
      // exponential backoff
      return delay + jitter;
    },
  };
  return newOptions;
};
